import { useId } from "react"

/**
 * Decorative botanical accent for the LEFT side of the hero, balancing the
 * right-side care-plan peek so the composition reads as deliberate asymmetry
 * rather than a slipped element. Grass blades grow up from below; a single
 * small floating leaf adds breath. No animations, no logic.
 */
export function HeroLeftAccent() {
  const idPrefix = `hero-left-${useId().replace(/:/g, "")}`
  const lightId = `${idPrefix}-blade-light`
  const darkId = `${idPrefix}-blade-dark`
  const glowId = `${idPrefix}-glow`

  return (
    <svg
      viewBox="0 0 200 280"
      role="presentation"
      aria-hidden="true"
      className="w-full h-auto"
    >
      <defs>
        <linearGradient id={lightId} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#16a34a" />
          <stop offset="100%" stopColor="#86efac" />
        </linearGradient>
        <linearGradient id={darkId} x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#14532d" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
        <radialGradient id={glowId} cx="50%" cy="80%" r="60%">
          <stop offset="0%" stopColor="#bbf7d0" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Soft glow behind the blades */}
      <ellipse cx="100" cy="220" rx="120" ry="80" fill={`url(#${glowId})`} />

      {/* Grass blades — five, varied heights, mixed light/dark */}
      <g>
        <path
          d="M 30 280 C 32 240, 26 200, 20 178"
          stroke={`url(#${darkId})`}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 56 280 C 58 246, 62 210, 68 188"
          stroke={`url(#${lightId})`}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 92 280 C 92 240, 86 200, 80 174"
          stroke={`url(#${lightId})`}
          strokeWidth="2.6"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 124 280 C 126 250, 132 218, 138 196"
          stroke={`url(#${darkId})`}
          strokeWidth="2.8"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 160 280 C 160 252, 154 222, 148 204"
          stroke={`url(#${lightId})`}
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* Tiny floating leaf — adds breath, not noise */}
      <g transform="translate(150, 90) rotate(-25)">
        <path
          d="M 0 0 C 8 -6, 18 -6, 22 4 C 18 14, 8 14, 0 8 Z"
          fill="#bbf7d0"
          stroke="#16a34a"
          strokeWidth="1"
        />
        <path
          d="M 4 4 L 18 4"
          stroke="#16a34a"
          strokeWidth="0.8"
          strokeLinecap="round"
        />
      </g>

      {/* Two faint sparkles for delight */}
      <g fill="#22c55e" opacity="0.5">
        <path d="M 50 110 l 1.4 2.8 l 2.8 1.4 l -2.8 1.4 l -1.4 2.8 l -1.4 -2.8 l -2.8 -1.4 l 2.8 -1.4 z" />
        <path d="M 110 150 l 1 2 l 2 1 l -2 1 l -1 2 l -1 -2 l -2 -1 l 2 -1 z" />
      </g>
    </svg>
  )
}
